import { Images, Sparkles, UserRound, Zap } from "lucide-react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { planName } from "../../shared/products";
import { useAuth } from "../auth";
import { useCapabilities } from "../capabilities";
import { Brand } from "./Brand";

const NAV_ITEMS: Array<{
  to: string;
  label: string;
  icon: typeof Sparkles;
  desktopOnly?: boolean;
  requiresAlipay?: boolean;
}> = [
  { to: "/generate", label: "AI 生成", icon: Sparkles },
  { to: "/artworks", label: "我的作品", icon: Images },
  { to: "/pricing", label: "次数购买", icon: Zap, desktopOnly: true, requiresAlipay: true },
  { to: "/account", label: "个人中心", icon: UserRound },
];

const PAGE_TITLES: Record<string, string> = {
  "/generate": "AI 壁纸工作台",
  "/artworks": "我的作品",
  "/pricing": "创作权益",
  "/account": "个人中心",
};

export function AppShell() {
  const location = useLocation();
  const { me, requestLogin } = useAuth();
  const { fubeiEnabled } = useCapabilities();
  const quota = me?.quota;
  const progress = quota ? Math.min(100, (quota.used / Math.max(1, quota.total)) * 100) : 0;

  return (
    <div className="app-shell">
      <a className="skip-link" href="#main-content">跳到主要内容</a>
      <aside className="sidebar">
        <Brand />
        <nav className="side-nav" aria-label="主要导航">
          {NAV_ITEMS.filter((item) => !item.requiresAlipay || fubeiEnabled).map(({ to, label, icon: Icon }) => (
            <NavLink key={to} to={to} className={({ isActive }) => (isActive ? "active" : undefined)}>
              <Icon size={18} aria-hidden="true" />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="sidebar-bottom">
          {quota ? (
            <div className="quota-card" aria-label="生成次数用量">
              <div className="quota-heading"><span>本期生成</span><strong>{quota.used} / {quota.total}</strong></div>
              <div className="quota-track"><i style={{ width: progress + "%" }} /></div>
              <div className="quota-caption"><span>{planName(me?.user.planCode ?? "FREE").replace(" 30 天权益包", "")}</span><b>{quota.available} 次可用</b></div>
            </div>
          ) : (
            <button className="button button-secondary sidebar-login" onClick={() => requestLogin(location.pathname)}>
              使用 KOOK 登录
            </button>
          )}
        </div>
      </aside>

      <div className="app-main">
        <header className="topbar">
          <div className="breadcrumbs"><span>GAMEFRAME</span><span>/</span><b>{PAGE_TITLES[location.pathname] ?? "创作空间"}</b></div>
          <div className="top-actions">
            {me ? (
              <NavLink className="profile-link" to="/account" aria-label="进入个人中心">
                {me.user.avatarUrl ? <img src={me.user.avatarUrl} alt="" /> : <span>{me.user.nickname.slice(0, 1).toUpperCase()}</span>}
                <b>{me.user.nickname}</b>
              </NavLink>
            ) : (
              <button className="button button-primary button-small" onClick={() => requestLogin(location.pathname)}>
                KOOK 登录
              </button>
            )}
          </div>
        </header>
        <main id="main-content" className="page-content"><Outlet /></main>
      </div>

      <nav className="mobile-nav" aria-label="移动端主要导航">
        {NAV_ITEMS.filter((item) => !item.desktopOnly && (!item.requiresAlipay || fubeiEnabled)).map(({ to, label, icon: Icon }) => (
          <NavLink key={to} to={to} className={({ isActive }) => (isActive ? "active" : undefined)}>
            <Icon size={19} aria-hidden="true" />
            <span>{label === "AI 生成" ? "生成" : label === "我的作品" ? "作品" : "我的"}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
